import { useQuery, useMutation } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { useAppStore } from "@/store";

type PermissionRegistry = components["schemas"]["PermissionRegistry"];
type MarkSeenResponse = components["schemas"]["MarkSeenResponse"];

export const permissionRegistryKeys = {
  all: ["permission-registry"] as const,
  byOrg: (orgId: string) => [...permissionRegistryKeys.all, orgId] as const,
};

/**
 * The closed permission vocabulary (domains → entities → legal ops). The
 * registry is compiled into the API and never changes at runtime, so cache it
 * effectively forever (staleTime Infinity). Keyed on orgId only because the
 * route is org-scoped; the payload is org-invariant.
 */
export function usePermissionRegistry() {
  const orgId = useAppStore((s) => s.activeOrgId);
  return useQuery<PermissionRegistry>({
    queryKey: permissionRegistryKeys.byOrg(orgId ?? ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.GET("/orgs/{orgId}/permission-registry", {
        params: { path: { orgId } },
      });
      if (error) {
        const e = error as { error?: string };
        throw new ApiError(response.status, e?.error ?? "Failed to load permission registry");
      }
      return data;
    },
    enabled: Boolean(orgId),
    staleTime: Infinity,
    gcTime: 24 * 60 * 60_000,
  });
}

/**
 * Advance the org's permission-registry last-seen watermark (`POST
 * /permission-registry/seen`). Deliberately does NOT invalidate the registry
 * query on its own success — the caller decides:
 *
 * - Open-advance (matrix mount): fire WITHOUT invalidating so the badges keep
 *   rendering from the already-fetched (pre-advance) registry on the visit that
 *   triggered the advance; they clear on the next load.
 * - Explicit "Mark all as seen": the caller passes an `onSuccess` that
 *   invalidates `permissionRegistryKeys.all` so the badges clear immediately.
 */
export function useMarkPermissionRegistrySeen() {
  const orgId = useAppStore((s) => s.activeOrgId);
  // TVariables defaults to void (this is a no-argument mutation) — spelling
  // `void` explicitly trips @typescript-eslint/no-invalid-void-type.
  return useMutation<MarkSeenResponse, ApiError>({
    mutationFn: async () => {
      if (!orgId) throw new Error("No org context");
      const { data, error, response } = await apiClient.POST(
        "/orgs/{orgId}/permission-registry/seen",
        { params: { path: { orgId } } },
      );
      if (error) {
        const e = error as { error?: string };
        throw new ApiError(response.status, e?.error ?? "Failed to mark registry seen");
      }
      // openapi-fetch types `data` as `body | undefined`; the error guard above
      // proves a defined 2xx body, so the cast pins the MarkSeenResponse shape.
      return data as MarkSeenResponse;
    },
  });
}

export type { PermissionRegistry, MarkSeenResponse };
